// react
import React, { useState, useEffect } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  useTheme,
  useMediaQuery,
  Paper,
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  IconButton,
  Tooltip,
  Button,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  CloseOutlined as CloseOutlinedIcon,
  Tag as TagIcon,
  Schema as SchemaIcon,
  PeopleAlt as PeopleAltIcon,
  Description as DescriptionIcon,
  Tune as TuneIcon,
  AccessTime as AccessTimeIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
  VideoLibrary as VideoLibraryIcon,
} from "@mui/icons-material";

// artifacts/cdn
import { metadataSchemaUpload, metadataSections } from "../generate/metadataSchema";
import DetailAccordion from "./DetailAccordion";

// components
import { Puller } from "components/common/Puller";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const DetailsDialog = ({ open, record, loading = false, onClose, handleOpenFile, handleDownloadFile }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [expandedDocuments, setExpandedDocuments] = useState(false);
  const [expandedMetadata, setExpandedMetadata] = useState(false);
  const [expandedSoggetti, setExpandedSoggetti] = useState(false);
  const [expandedExtraMetadata, setExpandedExtraMetadata] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) onClose();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open, onClose]);

  const onOpenDocument = (doc) => {
    const fileInfo = record.data?.find((f) => f.fileName === doc.nomeDocumento);
    if (fileInfo) handleOpenFile(fileInfo);
  };

  const onDownloadDocument = (doc) => {
    const fileInfo = record.data?.find((f) => f.fileName === doc.nomeDocumento);
    if (fileInfo) handleDownloadFile(fileInfo);
  };

  const labelSx = {
    fontSize: "0.72rem",
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
    fontWeight: 600,
    mb: 0.5,
  };

  const valueSx = {
    fontSize: "0.9rem",
    fontWeight: 500,
    color: theme.palette.text.primary,
    mb: 2,
    wordBreak: "break-word",
  };

  const SectionLabel = ({ children }) => <Typography sx={labelSx}>{children}</Typography>;

  const SectionValue = ({ children }) => <Typography sx={valueSx}>{children}</Typography>;

  const cardBaseSx = {
    p: 2,
    borderRadius: 4,
    backgroundColor: theme.palette.background.paper,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "0 4px 18px rgba(0,0,0,0.04)",
  };

  const metadataFields = metadataSections
    .filter((s) => s.title !== "Soggetti")
    .flatMap((s) => s.fields)
    .filter((fieldKey) => record[fieldKey] !== undefined && record[fieldKey] !== null);

  const soggettiSection = metadataSections.find((s) => s.title === "Soggetti");

  const getDocumentIcon = (format) => {
    if (!format) {
      return {
        icon: <DescriptionIcon fontSize="small" />,
        color: "default",
      };
    }

    const fmt = format.toLowerCase();

    if (fmt === "application/pdf") {
      return {
        icon: <DescriptionIcon fontSize="small" color="error" />,
        color: "error",
      };
    }

    if (fmt === "application/xml" || fmt === "text/xml") {
      return {
        icon: <SchemaIcon fontSize="small" color="info" />,
        color: "info",
      };
    }

    if (fmt === "application/json") {
      return {
        icon: <SchemaIcon fontSize="small" color="success" />,
        color: "success",
      };
    }

    if (fmt === "video/webm") {
      return {
        icon: <VideoLibraryIcon fontSize="small" color="warning" />,
        color: "warning",
      };
    }

    if (fmt === "text/plain" || fmt.endsWith("txt")) {
      return {
        icon: <DescriptionIcon fontSize="small" color="disabled" />,
        color: "default",
      };
    }

    return {
      icon: <DescriptionIcon fontSize="small" />,
      color: "default",
    };
  };

  const LoadingOverlay = () => (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 10,
        backgroundColor: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backdropFilter: "blur(4px)",
      }}
    >
      <Box
        sx={{
          px: 4,
          py: 3,
          borderRadius: 3,
          backgroundColor: "background.paper",
          boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography fontWeight={600}>{t("loading_details")}</Typography>
      </Box>
    </Box>
  );

  const DetailsContent = (
    <Grid container spacing={2} sx={{ p: 1 }}>
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            ...cardBaseSx,
            mb: 2,
            p: 2.5,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <TagIcon sx={{ opacity: 0.6 }} fontSize="small" />
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.3 }}>{t("upload_id")}</Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }}>{record.id}</Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccessTimeIcon sx={{ opacity: 0.6 }} fontSize="small" />
            <Box>
              <Typography sx={{ ...labelSx, mb: 0.3 }}>{t("upload_datetime")}</Typography>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 600 }}>{record.timestamp ? convertTimestamp(record.timestamp) : "N/A"}</Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>

      {Array.isArray(record.documenti) && record.documenti.length > 0 && (
        <Grid item xs={12}>
          <DetailAccordion
            icon={<DescriptionIcon sx={{ opacity: 0.6 }} />}
            title={t("documents")}
            count={record.documenti.length}
            expanded={expandedDocuments}
            onToggle={() => setExpandedDocuments((v) => !v)}
          >
            <Grid container spacing={2}>
              {record.documenti.map((doc, idx) => {
                const { icon, color } = getDocumentIcon(doc.formatoDocumento);

                return (
                  <Grid key={idx} item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                        border: "1px solid rgba(255,255,255,0.05)",
                        transition: "all 0.25s ease",
                        "&:hover": {
                          boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      <Grid container alignItems="center" spacing={1}>
                        <Grid item>{icon}</Grid>

                        <Grid item xs>
                          <Typography variant="body1" fontWeight={700}>
                            {doc.nomeDocumento}
                          </Typography>

                          <Chip
                            label={doc.formatoDocumento}
                            size="small"
                            color={color === "default" ? undefined : color}
                            sx={{
                              mt: 0.5,
                              height: 22,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              borderRadius: 3,
                              textTransform: "uppercase",
                            }}
                          />
                        </Grid>

                        <Grid item sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title={t("open")}>
                            <IconButton onClick={() => onOpenDocument(doc)}>
                              <OpenInNewIcon sx={{ opacity: 0.8 }} color="primary" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title={t("download")}>
                            <IconButton onClick={() => onDownloadDocument(doc)}>
                              <DownloadIcon sx={{ opacity: 0.8 }} color="primary" />
                            </IconButton>
                          </Tooltip>
                        </Grid>
                      </Grid>

                      <Box sx={{ mt: 2, pl: 0.5 }}>
                        <SectionLabel>{t("id")}</SectionLabel>
                        <SectionValue>{doc.idDocumento}</SectionValue>

                        <SectionLabel>{t("hash")}</SectionLabel>
                        <SectionValue>{doc.hash}</SectionValue>

                        <SectionLabel>{t("algorithm")}</SectionLabel>
                        <SectionValue>{doc.algoritmo}</SectionValue>

                        <SectionLabel>{t("number_of_attachments")}</SectionLabel>
                        <SectionValue>{doc.numeroAllegati}</SectionValue>

                        {doc.descrizioneAllegato && (
                          <>
                            <SectionLabel>{t("attachment_description")}</SectionLabel>
                            <SectionValue>{doc.descrizioneAllegato}</SectionValue>
                          </>
                        )}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </DetailAccordion>
        </Grid>
      )}

      {metadataFields.length > 0 && (
        <Grid item xs={12}>
          <DetailAccordion icon={<SchemaIcon sx={{ opacity: 0.6 }} />} title={t("Metadata")} count={metadataFields.length} expanded={expandedMetadata} onToggle={() => setExpandedMetadata((v) => !v)}>
            <Grid container spacing={2}>
              {metadataFields.map((fieldKey, idx) => (
                <Grid item xs={12} sm={6} key={idx}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.paper,
                      border: "1px solid rgba(255,255,255,0.05)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                    }}
                  >
                    <SectionLabel>{t(`metadata.${fieldKey.toLowerCase()}`)}</SectionLabel>
                    <SectionValue>{fieldKey === "dataInvioInConservazione" ? convertTimestamp(record[fieldKey]) : record[fieldKey]}</SectionValue>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DetailAccordion>
        </Grid>
      )}

      {soggettiSection && Array.isArray(record.soggetti) && record.soggetti.length > 0 && (
        <Grid item xs={12}>
          <DetailAccordion
            icon={<PeopleAltIcon sx={{ opacity: 0.6 }} />}
            title={t("subjects")}
            count={record.soggetti.length}
            expanded={expandedSoggetti}
            onToggle={() => setExpandedSoggetti((v) => !v)}
          >
            <Grid container spacing={2}>
              {record.soggetti.map((soggetto, idx) => (
                <Grid key={idx} item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                      border: "1px solid rgba(255,255,255,0.04)",
                    }}
                  >
                    <Typography variant="body1" fontWeight={700} sx={{ mb: 2, opacity: 0.9 }}>
                      {t("subject")} {idx + 1}
                    </Typography>

                    {Object.keys(soggetto).map((key) =>
                      soggetto[key] ? (
                        <Box key={key} sx={{ mb: 1.5 }}>
                          <SectionLabel>
                            {t(`metadata.${key.toLowerCase()}`, {
                              defaultValue: metadataSchemaUpload[key]?.label || key,
                            })}
                          </SectionLabel>
                          <SectionValue>{soggetto[key]}</SectionValue>
                        </Box>
                      ) : null
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DetailAccordion>
        </Grid>
      )}

      {record.extraMetadata && Object.keys(record.extraMetadata).length > 0 && (
        <Grid item xs={12}>
          <DetailAccordion
            icon={<TuneIcon sx={{ opacity: 0.6 }} />}
            title={t("extra_metadata")}
            count={Object.keys(record.extraMetadata).length}
            expanded={expandedExtraMetadata}
            onToggle={() => setExpandedExtraMetadata((v) => !v)}
          >
            <Grid container spacing={2}>
              {Object.entries(record.extraMetadata).map(([key, value]) => (
                <Grid item xs={12} sm={6} key={key}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      backgroundColor: theme.palette.background.paper,
                      boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                      border: "1px solid rgba(255,255,255,0.04)",
                      transition: "all 0.25s ease",
                      "&:hover": {
                        boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <SectionLabel>{key}</SectionLabel>
                    <SectionValue>{value}</SectionValue>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </DetailAccordion>
        </Grid>
      )}
    </Grid>
  );

  return (
    <>
      {isMobile ? (
        <SwipeableDrawer
          anchor="bottom"
          open={open}
          onClose={onClose}
          onOpen={() => {}}
          sx={{
            "& .MuiDrawer-paper": {
              width: "100%",
              height: "90%",
              borderTopLeftRadius: "4%",
              borderTopRightRadius: "4%",
            },
          }}
        >
          <Puller />

          <Box sx={{ p: 3, position: "relative" }}>
            {loading && <LoadingOverlay />}

            <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
              {t("details")}
            </Typography>

            {DetailsContent}
          </Box>
        </SwipeableDrawer>
      ) : (
        <Dialog
          open={open}
          onClose={onClose}
          maxWidth="md"
          fullWidth
          sx={{
            "& .MuiPaper-root": {
              borderRadius: 4,
              background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
              transition: "all 0.3s ease",
            },
          }}
        >
          <DialogTitle>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item>
                <Typography variant="h6" fontWeight={700}>
                  {t("details")}
                </Typography>
              </Grid>
              <Grid item>
                <Tooltip title={t("close")} placement="top">
                  <IconButton onClick={onClose} color="error" edge="end">
                    <CloseOutlinedIcon />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </DialogTitle>

          <DialogContent sx={{ position: "relative", minHeight: 300 }}>
            {loading && <LoadingOverlay />}
            {DetailsContent}
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              variant="text"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={onClose}
            >
              {t("close")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

DetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  record: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  handleOpenFile: PropTypes.func.isRequired,
  handleDownloadFile: PropTypes.func.isRequired,
};

export default DetailsDialog;
